'use client';
// L'analyse de cause d'une NC en CASES : fait constaté, cause retenue, Ishikawa COCHÉ.
// Les étiologies et les explications viennent de `lib/analyse-capa` — une seule source
// avec le test serveur. Les « 5 pourquoi » ont été retirés de la saisie (RQ, 08/10/2026) :
// une analyse qui en portait s'ouvre avec son texte repris en « autres éléments ».
import React from 'react';
import { inputStyle } from '@/components/ui';
import {
  ISHIKAWA, FAIT_CONSTATE, CAUSE_RETENUE, LIBRE, CLES_M, familleDe, libelleEtiologie,
  basculerEtiologie, depuisNc, type AnalyseCapa,
} from '@/lib/analyse-capa';

const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
  color: 'var(--text-muted)', marginBottom: 4,
};
const aide: React.CSSProperties = { margin: '0 0 6px', fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-muted)' };
const boite: React.CSSProperties = { ...inputStyle, height: 'auto', padding: '8px 11px', resize: 'vertical', width: '100%' };
const titre: React.CSSProperties = { margin: '0 0 4px', fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' };
const carte: React.CSSProperties = {
  border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 12px',
  background: 'var(--surface-card)',
};
const compteur: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '1px 8px',
  background: 'var(--cyan-50)', color: 'var(--cyan-700)',
};

export function AnalyseCauseEditeur({ valeur, onChange }: { valeur: AnalyseCapa; onChange: (a: AnalyseCapa) => void }) {
  const maj = (p: Partial<AnalyseCapa>) => onChange({ ...valeur, ...p });
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div>
        <label style={lbl} htmlFor="an-fait">{FAIT_CONSTATE.libelle}</label>
        <p style={aide}>{FAIT_CONSTATE.explication}</p>
        <textarea id="an-fait" rows={2} value={valeur.fait} onChange={(e) => maj({ fait: e.target.value })} style={boite} />
      </div>

      <div>
        <h4 style={titre}>{ISHIKAWA.titre}</h4>
        <p style={aide}>{ISHIKAWA.explication}</p>
        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
          {ISHIKAWA.familles.map((f) => {
            const coches = valeur.ishikawa[f.cle] || [];
            return (
              <fieldset key={f.cle} style={{ ...carte, margin: 0, minInlineSize: 0 }}>
                <legend style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '0 4px' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>{f.libelle}</span>
                  {coches.length > 0 && <span style={compteur}>{coches.length}</span>}
                </legend>
                <p style={{ ...aide, fontSize: 12, margin: '0 0 6px' }}>{f.explication}</p>
                <div style={{ display: 'grid', gap: 3 }}>
                  {f.etiologies.map((et) => {
                    const coche = coches.includes(et.code);
                    return (
                      <label key={et.code} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer',
                        fontSize: 12.5, lineHeight: 1.4, padding: '3px 4px', borderRadius: 'var(--radius-sm)',
                        background: coche ? 'var(--cyan-50)' : 'transparent',
                        color: coche ? 'var(--text-strong)' : 'var(--text-body)',
                        fontWeight: coche ? 600 : 400,
                      }}>
                        <input type="checkbox" checked={coche} style={{ accentColor: 'var(--color-primary)', marginTop: 2 }}
                          onChange={() => onChange(basculerEtiologie(valeur, f.cle, et.code))} />
                        <span>{et.libelle}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            );
          })}
        </div>
      </div>

      <div>
        <label style={lbl} htmlFor="an-cause">{CAUSE_RETENUE.libelle}</label>
        <p style={aide}>{CAUSE_RETENUE.explication}</p>
        <textarea id="an-cause" rows={2} value={valeur.causeRetenue} onChange={(e) => maj({ causeRetenue: e.target.value })} style={boite} />
      </div>

      <details open={!!valeur.libre.trim()}>
        <summary style={{ cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)' }}>
          {LIBRE.titre}
        </summary>
        <p style={{ ...aide, marginTop: 6 }}>{LIBRE.explication}</p>
        <textarea rows={3} value={valeur.libre} aria-label={LIBRE.titre} onChange={(e) => maj({ libre: e.target.value })} style={boite} />
      </details>
    </div>
  );
}

/** Lecture : les cases remplies, rien d'autre. Sans cases, le texte d'origine tel quel. */
export function AnalyseCauseLecture({ analyse, causes }: { analyse: unknown; causes: string }) {
  const aCases = !!analyse && typeof analyse === 'object' && !Array.isArray(analyse);
  const vide = <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-muted)' }}>Non renseignée.</p>;
  if (!aCases) {
    return causes
      ? <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-body)', whiteSpace: 'pre-wrap' }}>{causes}</p>
      : vide;
  }
  // Une analyse de l'ancienne forme (des « pourquoi », un Ishikawa en texte) se lit
  // migrée, comme l'éditeur l'ouvrirait : son texte est repris, jamais perdu.
  const a = depuisNc({ analyse, causes });
  const bloc = (t: string, v?: string) => (v && v.trim() ? (
    <div key={t}><div style={lbl}>{t}</div>
      <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-body)', whiteSpace: 'pre-wrap' }}>{v}</p></div>
  ) : null);
  const ms = CLES_M.filter((k) => a.ishikawa[k].length).map((k) => (
    <div key={k} style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-body)' }}>
      <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{familleDe(k).libelle} : </span>
      {a.ishikawa[k].map((c) => libelleEtiologie(k, c)).join(' ; ')}
    </div>
  ));
  const blocs = [bloc(FAIT_CONSTATE.libelle, a.fait), bloc(CAUSE_RETENUE.libelle, a.causeRetenue),
    ms.length ? <div key="m" style={{ display: 'grid', gap: 4 }}><div style={lbl}>Ishikawa</div>{ms}</div> : null,
    bloc('Autres éléments', a.libre)].filter(Boolean);
  return blocs.length ? <div style={{ display: 'grid', gap: 10 }}>{blocs}</div> : vide;
}
